import { ReactElement, useState } from 'react';

import { UploadResult } from 'firebase/storage';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Fade from '@mui/material/Fade';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Slide from '@mui/material/Slide';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import Org from '../../types/Org';

import OrgCardPreview from './OrgCardPreview';
import OrgDefaultsEditor from './OrgDefaultsEditor';
import OrgGeneralSettingsEditor from './OrgGeneralSettingsEditor';
import { OrgPreviewElement, OrgEditorSection, ORG_ELEMENT_TO_EDITOR } from './orgPreviewTypes';

type EditorSection = OrgEditorSection;

export interface OrgReviewAndFinalizeProps {
  org: Org;
  isNew?: boolean;
  isEditing?: boolean;
  onOrgChange: (org: Org) => void;
  onEditingClose?: () => void;
  onEditingOpen?: () => void;
  onImageUpdated?: (snapshot: UploadResult | null) => void;
  onFileSelected?: (file: File | null) => void;
}

export default function OrgReviewAndFinalize(props: OrgReviewAndFinalizeProps): ReactElement {
  const {
    org,
    isNew = true,
    isEditing = false,
    onOrgChange,
    onEditingClose,
    onEditingOpen,
    onImageUpdated,
    onFileSelected,
  } = props;

  const [expandedSection, setExpandedSection] = useState<EditorSection | false>('general');

  const handleAccordionChange = (panel: EditorSection) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedSection(isExpanded ? panel : false);
  };

  const handleElementClick = (element: OrgPreviewElement) => {
    const mapping = ORG_ELEMENT_TO_EDITOR[element];

    // Open editing panel if not already open
    if (!isEditing && onEditingOpen) {
      onEditingOpen();
    }

    // Expand the appropriate accordion section
    setExpandedSection(mapping.section);

    // Focus the field after a brief delay to allow accordion to expand
    if (mapping.field) {
      setTimeout(() => {
        const fieldElement = document.getElementById(mapping.field!);
        if (fieldElement) {
          fieldElement.focus();
          fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
    }
  };

  const handleUpdate = <K extends keyof Org>(field: K, value: Org[K]) => {
    onOrgChange({ ...org, [field]: value });
  };

  return (
    <Grid container spacing={3} sx={{ pt: 2, alignItems: 'stretch', overflow: 'hidden' }}>
      {/* Preview Card - Full width when not editing, left half when editing on md+ */}
      <Grid
        size={{ xs: 12, md: isEditing ? 5 : 12 }}
        sx={{
          transition: 'all 300ms ease-in-out',
        }}
      >
        <Stack spacing={2}>
          <Fade in timeout={400}>
            <Stack spacing={0.5} sx={{
              alignItems: "flex-start"
            }}>
              <Typography variant="body1" sx={{
                color: "text.secondary"
              }}>
                This is how the organization will appear to members.
              </Typography>
              {!isEditing && onEditingOpen && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={onEditingOpen}
                >
                  Edit
                </Button>
              )}
            </Stack>
          </Fade>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box sx={{ maxWidth: '27.75rem', width: '100%' }}>
              <OrgCardPreview
                org={org}
                variant="outlined"
                onElementClick={handleElementClick}
              />
            </Box>
          </Box>
        </Stack>
      </Grid>
      {/* Edit Sections - Always rendered for animation, visibility controlled by Slide */}
      <Grid
        size={{ xs: 12, md: isEditing ? 7 : 0 }}
        sx={{
          display: 'flex',
          transition: 'all 300ms ease-in-out',
          overflow: 'hidden',
        }}
      >
        <Slide direction="left" in={isEditing} mountOnEnter unmountOnExit>
          <Box
            sx={{
              borderLeft: 2,
              borderColor: 'divider',
              pl: 2,
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Stack spacing={1}>
              <Stack
                direction="row"
                sx={{
                  alignItems: "center",
                  justifyContent: "space-between"
                }}>
                <Typography variant="body2" sx={{
                  color: "text.secondary"
                }}>
                  Tap a section to make changes.
                </Typography>
                {onEditingClose && (
                  <IconButton onClick={onEditingClose} size="small" aria-label="Close edit panel">
                    <CloseIcon />
                  </IconButton>
                )}
              </Stack>

              {/* General Settings */}
              <Accordion
                expanded={expandedSection === 'general'}
                onChange={handleAccordionChange('general')}
                disableGutters
                elevation={0}
                sx={{
                  '&:before': { display: 'none' },
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">General Settings</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <OrgGeneralSettingsEditor
                    org={org}
                    isNew={isNew}
                    onUpdate={handleUpdate}
                    onImageUpdated={onImageUpdated}
                    onFileSelected={onFileSelected}
                  />
                </AccordionDetails>
              </Accordion>

              {/* Defaults */}
              <Accordion
                expanded={expandedSection === 'defaults'}
                onChange={handleAccordionChange('defaults')}
                disableGutters
                elevation={0}
                sx={{
                  '&:before': { display: 'none' },
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">Defaults</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <OrgDefaultsEditor
                    org={org}
                    onUpdate={handleUpdate}
                  />
                </AccordionDetails>
              </Accordion>
            </Stack>
          </Box>
        </Slide>
      </Grid>
    </Grid>
  );
}
